/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useAuth } from 'contexts/AuthContext';
import {
  EventSummaryResponse,
  GroupResponse,
  InitiateSignInResponse,
  OrganisationResponse,
  PagedOrganisationResponse,
  WalletResponse,
  useGetAllEvents,
  useGetAllGroups,
  useGetOrgsWithRevokeAccessFlag,
  useGetPractitionerOrganisations,
  useGetPractitionerWallet,
  useGetSignInType,
} from 'api';
import useGetMyOrganisations from 'components/hooks/useGetMyOrganisations';
import { queryStatus } from 'utils/queryStatus';
import { sortCancelledExperiences } from 'utils/mappers/sorting';
import { createGroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import { isSuperAdmin } from 'utils/role';

type AccountOrganisation = PagedOrganisationResponse &
  Pick<OrganisationResponse, 'canRevokeAccess'>;

const useGetAccountData = (): {
  isPending: boolean;
  isError: boolean;
  activeEvents: EventSummaryResponse[] | undefined;
  cancelledEvents: EventSummaryResponse[];
  wallet: WalletResponse | undefined;
  organisation: AccountOrganisation | undefined;
  allMyOrganisations: AccountOrganisation[] | undefined;
  emailAddress: string;
  practitionerOrganisations: OrganisationResponse[] | undefined;
  groups: GroupResponse[] | undefined;
  organisationsInGroups: Record<string, GroupResponse>;
  signInMethod: InitiateSignInResponse | undefined;
} => {
  const { user } = useAuth();
  const shouldGetOrgsWithRevokeAccess = !!user?.id && isSuperAdmin(user.role);
  const canViewGroups = user?.permissions.Organisation_Group_ReadAll !== 'None';
  const { status: getOrganisationStatus, organisations: allMyOrganisations } =
    useGetMyOrganisations({
      load: shouldGetOrgsWithRevokeAccess ? 'none' : 'allPages',
    });

  const { status: getEventsStatus, data: activeEvents } = useGetAllEvents({
    status: 'ACTIVE',
  });

  const { data: cancelledEvents = [] } = useGetAllEvents({
    status: 'CANCELLED',
  });
  const { data: groups, status: groupStatus } = useGetAllGroups({
    enabled: canViewGroups,
  });

  const { status: getWalletStatus, data: wallet } = useGetPractitionerWallet(
    user!.id,
  );
  const { emailAddress } = user!;
  const { status: practitionerOrganisationsStatus, data: organisations } =
    useGetPractitionerOrganisations(user?.id, {
      enabled: !!user?.id,
    });

  const organisationsInGroups = createGroupOrganisationHashMap(groups ?? []);

  const { data: revokeAccessOrgs, status: revokeAccessOrgsStatus } =
    useGetOrgsWithRevokeAccessFlag(user?.id || '', {
      enabled: shouldGetOrgsWithRevokeAccess,
    });

  const myOrgs = shouldGetOrgsWithRevokeAccess
    ? revokeAccessOrgs
    : allMyOrganisations;

  const practitionerOrganisations = organisations?.filter(
    ({ id }) => !organisationsInGroups[id],
  );

  const fetchOrgsStatus = shouldGetOrgsWithRevokeAccess
    ? revokeAccessOrgsStatus
    : getOrganisationStatus;
  const { status: signInMethodStatus, data: signInMethod } =
    useGetSignInType(emailAddress);

  const status = queryStatus(
    fetchOrgsStatus,
    getEventsStatus,
    getWalletStatus,
    practitionerOrganisationsStatus,
    canViewGroups ? groupStatus : 'success',
    signInMethodStatus,
  );

  return {
    isPending: status === 'pending',
    isError: status === 'error',
    activeEvents,
    cancelledEvents: sortCancelledExperiences(cancelledEvents),
    wallet,
    organisation: myOrgs?.[0],
    allMyOrganisations: myOrgs,
    emailAddress,
    practitionerOrganisations,
    groups,
    organisationsInGroups,
    signInMethod,
  };
};
export default useGetAccountData;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
